import classNames from "classnames";
import { useDateFormatter } from "client/hooks/useDateFormatter";
import React, { useContext, useEffect, useMemo, useRef } from "react";
import ArtifactCard from "../../../components/Cards/ArtifactCard";
import Loader from "../../../components/Loader";
import Pagination from "../../../components/Pagination/index";
import Table from "../../../components/Table";
import { ClaimsColumns } from "../../../components/Table/TableColumns";
import { FilterClaimContext } from "../../../context";
import { ClaimFilters } from "../../../context/FilterClaimContext/helpers";
import history from "../../../context/history";
import { usePartnerUserDispatch } from "../../../context/PartnerUser";
import { updateCurrentClaim } from "../../../context/PartnerUser/actions";
import { isLgQuery } from "../../../helpers/mediaQuery";
import { ArtifactType, FormattedClaim } from "../../../types";
import { mountBodyGray6, unmountBodyGray6 } from "../../../utils/bodyStyle";
import getItemClaimRow from "../../../utils/getItemClaimRow";
import { Filters, NoItems } from "./components";
import { Wrapper } from "./styles";

const Claims = () => {
  const itemsPerPage = useRef<number>(12);
  const partnerUserDispatch = usePartnerUserDispatch();
  const {
    formattedClaims,
    claimsCount,
    categoryFilters,
    statusFilters,
    filters,
    setFilters,
    isLoading,
    page,
    setPage,
  } = useContext(FilterClaimContext);
  const isLg = isLgQuery();
  const dateFormatter = useDateFormatter();

  const rows = useMemo(() => {
    return (formattedClaims || [])?.map((claim, i) =>
      getItemClaimRow(claim as FormattedClaim, ArtifactType.Claim, i, dateFormatter),
    );
  }, [formattedClaims]);

  useEffect(() => {
    // Used to make the body gray6 (default it is white)
    mountBodyGray6();
    return function cleanup() {
      unmountBodyGray6();
    };
  }, []);

  return isLoading ? (
    <Loader />
  ) : (
    <Wrapper className={classNames("mb-5 pt-lg-35 px-0")}>
      <Filters
        categoryFilters={categoryFilters}
        statusFilters={statusFilters}
        filters={filters}
        setFilters={(filters: ClaimFilters) => {
          setPage(1);
          setFilters(filters);
        }}
      />
      <Pagination
        currentPage={page}
        totalCount={claimsCount}
        pageSize={itemsPerPage.current}
        onPageChange={page => setPage(page)}
        showPagination={formattedClaims && formattedClaims.length > 0}
      >
        {rows.length === 0 ? (
          <NoItems />
        ) : isLg ? (
          <Table
            columns={ClaimsColumns}
            artifactIds={(formattedClaims || [])?.map(claim => claim.id)}
            rows={rows}
            justifyEnd
            onRowClick={async claimId => {
              const claimFromId = formattedClaims?.find(claim => claim.id === claimId);
              claimFromId !== undefined &&
                updateCurrentClaim(partnerUserDispatch, claimFromId.originalClaim);
              history.push(`/partner/claim/${String(claimId)}`);
            }}
          />
        ) : (
          <>
            {(formattedClaims || []).map(claim => {
              // Reduce the amount of info passed through
              const { originalClaim, ...formattedClaim } = claim;
              return (
                <ArtifactCard
                  artifact={formattedClaim}
                  key={claim.id}
                  lostOrFoundLabel={"lost"}
                  onClick={() => {
                    updateCurrentClaim(partnerUserDispatch, originalClaim);
                    history.push(`/partner/claim/${claim?.id}`);
                  }}
                  isClaim
                />
              );
            })}
          </>
        )}
      </Pagination>
    </Wrapper>
  );
};

export default Claims;
